import { useEffect, useState } from "react";

import { getMenuItems } from "services/menu/rest";
import { MenuItemListDto } from "services/menu/restModels";

// Максимум, который отдаёт бэк за раз.
const ORDER_PAGE_SIZE = 100;

export interface UseAllCategoryItemsResult {
    items: MenuItemListDto[];
    loading: boolean;
    error: boolean;
}

/*
* Все позиции категории, страница за страницей. Нужен диалогу сортировки: порядок
* сохраняется списком id целиком, и по загруженным на экране страницам его не собрать —
* бэк отклонит неполный список. В обычном списке экрана постраничность остаётся.
* */
export const useAllCategoryItems = (
    menuId: number,
    menuCategoryId: number,
    // Загружаем не раньше, чем список реально понадобится: в настройках категории это
    // отдельная вкладка, и открывать её будут далеко не каждый раз.
    enabled: boolean
): UseAllCategoryItemsResult => {
    const [items, setItems] = useState<MenuItemListDto[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<boolean>(false);

    useEffect(() => {
        if (!enabled) return;

        let ignore = false;

        setLoading(true);
        setError(false);

        const loadAll = async (): Promise<MenuItemListDto[]> => {
            const collected: MenuItemListDto[] = [];
            // total приходит только с первой страницей — до неё границы не знаем.
            let total = Number.POSITIVE_INFINITY;

            while (collected.length < total) {
                const page = await getMenuItems({
                    menuId,
                    categoryId: menuCategoryId,
                    filter: "all",
                    skip: collected.length,
                    limit: ORDER_PAGE_SIZE,
                });

                if (!page) throw new Error("Не удалось загрузить позиции категории");

                if (page.total !== null) total = page.total;

                // Страховка от бесконечного цикла, если бэк вернул пустую страницу
                // раньше заявленного total.
                if (page.items.length === 0) break;

                collected.push(...page.items);
            }

            return collected;
        };

        loadAll()
            .then((result) => {
                if (ignore) return;
                setItems(result);
            })
            .catch(() => {
                if (ignore) return;
                setError(true);
                setItems([]);
            })
            .finally(() => {
                if (ignore) return;
                setLoading(false);
            });

        return () => {
            ignore = true;
        };
    }, [menuId, menuCategoryId, enabled]);

    return { items, loading, error };
};
